import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export type DebouncedFunction<TArgs extends unknown[]> = ((
	...args: TArgs
) => void) & {
	cancel: () => void;
};

/**
 * Debounce a function to limit the rate at which it can fire
 */
export function debounce<TArgs extends unknown[]>(
	func: (...args: TArgs) => void | Promise<void>,
	wait: number,
): DebouncedFunction<TArgs> {
	let timeout: ReturnType<typeof setTimeout> | null = null;

	const debounced = (...args: TArgs) => {
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(() => {
			func(...args);
		}, wait);
	};

	debounced.cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	};

	return debounced;
}

export function resolveMediaUrl(url?: string | null): string {
	if (!url) return "";

	const withPrivateStorageVersion = (value: string): string => {
		if (!value.includes("/api/v1/storage/")) return value;
		const separator = value.includes("?") ? "&" : "?";
		return `${value}${separator}v=private-storage-v2`;
	};

	if (
		url.startsWith("http://") ||
		url.startsWith("https://") ||
		url.startsWith("data:") ||
		url.startsWith("blob:")
	) {
		return withPrivateStorageVersion(url);
	}

	const apiBase = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
	if (!apiBase) return withPrivateStorageVersion(url);

	const resolved = url.startsWith("/")
		? `${apiBase}${url}`
		: `${apiBase}/${url}`;

	return withPrivateStorageVersion(resolved);
}
